import clsx from 'clsx';
import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { MeterBarChartPlotArea } from './components/MeterBarChartPlotArea.js';
import { MeterBarChartThresholdLegend } from './components/MeterBarChartThresholdLegend/MeterBarChartThresholdLegend.js';
import { METER_BAR_CHART_ARIA_LABEL } from './constants.js';
import { useMeterBarChartConfig } from './hooks/useMeterBarChartConfig.js';
import { MeterBarChartInternalDataProviders } from './providers/MeterBarChartInternalDataProviders.js';
import { MeterBarChartProviders } from './providers/MeterBarChartProviders.js';
import { Label } from './slots/Label.js';
import { Legend } from './slots/Legend.js';
import { Max } from './slots/Max.js';
import { Min } from './slots/Min.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { ThresholdLegend } from './slots/ThresholdLegend.js';
import { Tooltip } from './slots/Tooltip.js';
import { Value } from './slots/Value.js';
import type { MeterBarChartConsumerConfig } from './types/meter-bar-chart-config.js';
import { type MeterBarChartProps } from './types/meter-bar-chart.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { Intent } from '../../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../../core/styles/defaultChartValues.sty.js';
import { DEFAULT_VALUE_ACCESSOR } from '../../core/constants.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import { ChartType } from '../../core/types/chart-types.js';
import type { ComponentWithName } from '../../core/types/component-with-name.js';
import { MeterBarChartFormattedValue } from '../shared/components/formatted-value/MeterBarChartFormattedValue.js';
import { MeterBarLayout } from '../shared/components/meter-bar-layout/MeterBarLayout.js';
import { Toolbar } from '../shared/slots/Toolbar.js';
import type { MeterBarChartElementRef } from '../shared/types/meter-bars.js';
import { getNumericValue } from '../shared/utils/get-numeric-value.js';

const COMPONENT_NAME = 'SingleMeterBarChart';
/**
 * Single meter bar chart component */
const MeterBarChartRoot = forwardRef<
  MeterBarChartElementRef,
  PropsWithChildren<MeterBarChartProps>
>((props, forwardedRef) => {
  const {
    width,
    height,
    value,
    name,
    color,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'single-meter-bar-chart-root',
    prefixIcon,
    valueAccessor,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const config = useMeterBarChartConfig(props);
  const {
    size,
    thresholds,
    thresholdsIndicators,
    loading,
    min,
    max,
    minSlot,
    maxSlot,
    valueSlot,
    labelSlot,
    tooltip,
    formatter,
    unit,
  } = config;

  const meterBarChartRef = useRef<HTMLDivElement>(null);

  const hasLegend = thresholds.legend ?? thresholdsIndicators.legend;

  const consumerConfig: MeterBarChartConsumerConfig & {
    type: ChartType.SingleMeterBar;
  } = {
    type: ChartType.SingleMeterBar,
    thresholdsIndicators,
    size,
    min,
    max,
    formatter,
    unit,
    tooltip: Boolean(tooltip),
    loading,
  };

  const displayValue = getNumericValue(
    value,
    valueAccessor ?? DEFAULT_VALUE_ACCESSOR,
  );
  const internalDataProvidersProps = {
    value,
    displayValue,
    valueAccessor,
    name,
    color,
  };

  return (
    <MeterBarChartProviders config={config} seriesActions={props.seriesActions}>
      <MeterBarLayout
        {...behavioralTrackingProps}
        ref={meterBarChartRef}
        size={size}
        width={width}
        height={height}
        className={clsx(defaultChartSize, className)}
        style={style}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-testid={dataTestId}
        data-dt-component={COMPONENT_NAME}
        aria-label={METER_BAR_CHART_ARIA_LABEL}
        consumerConfig={consumerConfig}
        userRef={forwardedRef}
      >
        <MeterBarLayout.Providers
          providerName={MeterBarChartInternalDataProviders}
          providerProps={internalDataProvidersProps}
        />

        {prefixIcon && <MeterBarLayout.Icon>{prefixIcon}</MeterBarLayout.Icon>}
        {labelSlot && <MeterBarLayout.Label>{labelSlot}</MeterBarLayout.Label>}
        {valueSlot && (
          <MeterBarLayout.Value>
            <MeterBarChartFormattedValue
              value={displayValue}
              total={max}
              auto={valueSlot?.auto}
            >
              {valueSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Value>
        )}
        <MeterBarLayout.Chart>
          <MeterBarChartPlotArea />
        </MeterBarLayout.Chart>
        {minSlot && (
          <MeterBarLayout.Min>
            <MeterBarChartFormattedValue value={min} auto={minSlot?.auto}>
              {minSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Min>
        )}
        {maxSlot && (
          <MeterBarLayout.Max>
            <MeterBarChartFormattedValue value={max} auto={maxSlot?.auto}>
              {maxSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Max>
        )}
        {hasLegend && (
          <MeterBarLayout.Legend>
            <MeterBarChartThresholdLegend />
          </MeterBarLayout.Legend>
        )}
      </MeterBarLayout>
    </MeterBarChartProviders>
  );
});

(MeterBarChartRoot as ComponentWithName<typeof MeterBarChartRoot>).displayName =
  'MeterBarChart';

/**
 * The `MeterBarChart` provides a lightweight and simple visualization progress
 * toward a completion, consumption of a total, etc.  It consists of a horizontal
 * bar that gradually fills up with color or shading as it approaches the max value.
 * @public
 */
export const MeterBarChart = Object.assign(MeterBarChartRoot, {
  Value,
  Label,
  Min,
  Max,
  ThresholdIndicator,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  ThresholdLegend,
  Legend,
  Tooltip,
  ColorRule,
  Intent,
  Toolbar,
});
